import React, { useState } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { Sparkle, ArrowRight, Crown, Star, Eye, ShieldCheck, ShieldWarning, Sun } from "@phosphor-icons/react";
import { toast } from "sonner";
import KundaliChart from "../components/KundaliChart";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PaywallGate from "../components/PaywallGate";

const INTENSITY_TINT = {
  very_high: { bg: "from-amber-50 to-yellow-100", border: "border-amber-300", badge: "bg-amber-300 text-amber-950", icon: "text-amber-700", label: "VERY HIGH" },
  high: { bg: "from-emerald-50 to-teal-50", border: "border-emerald-300", badge: "bg-emerald-200 text-emerald-900", icon: "text-emerald-700", label: "HIGH" },
  moderate: { bg: "from-indigo-50 to-violet-50", border: "border-indigo-300", badge: "bg-indigo-200 text-indigo-900", icon: "text-indigo-700", label: "MODERATE" },
  warning: { bg: "from-rose-50 to-orange-50", border: "border-rose-300", badge: "bg-rose-200 text-rose-900", icon: "text-rose-700", label: "WEAK" },
};

function YogaCard({ y }) {
  const tint = INTENSITY_TINT[y.intensity] || INTENSITY_TINT.high;
  const Icon = y.intensity === "warning" ? ShieldWarning : y.intensity === "very_high" ? Crown : Sparkle;
  return (
    <article className={`rounded-2xl overflow-hidden border ${tint.border}`} data-testid={`yoga-card-${y.key}`}>
      <div className={`p-6 bg-gradient-to-br ${tint.bg}`}>
        <div className="flex items-baseline gap-3 flex-wrap">
          <Icon size={18} weight="duotone" className={tint.icon} />
          <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${tint.badge}`}>{tint.label}</span>
          {y.chart && <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/70 border border-soft text-[#3F2D1B]">{y.chart}</span>}
          <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{y.name}</h3>
        </div>
        <p className="text-xs text-[#3F2D1B]/80 mt-3 italic"><b>Why this fires for you:</b> {y.why}</p>
        <p className="text-sm mt-4 font-light leading-snug text-[#3F2D1B]">{y.meaning}</p>
      </div>
    </article>
  );
}

export default function RajaYogasPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/raja-yogas/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='raja-yogas-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute Raja Yogas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Crown size={14} weight="duotone" /> Raja Yogas · D1 + D9 · Vedic + Jaimini
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Royal yogas in your <span className="text-gold">birth chart</span>
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-3xl">
            Detects classical Parashari raja yogas — <b>Gajakesari, Panchamahapurusha</b> (Ruchaka, Bhadra, Hamsa, Malavya, Sasa), <b>Chandra-Mangal, Budha-Aditya, Neech Bhanga, Dharma-Karmadhipati, Kendra-Trikona, Vipreet, Lakshmi, Adhi</b> — across your D1 (Rasi) AND D9 (Navamsa) charts. Then computes your <b>Jaimini Chara Karakas</b> (Atmakaraka → Darakaraka), <b>Karakamsa Lagna</b> interpretation, and Jaimini raja yogas (AK+AmK, Bhogya, Mind-Soul Union).
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="raja-yogas-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="raja-yogas-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="raja-yogas-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="raja-yogas-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`raja-yogas-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="raja-yogas-submit">
            {loading ? "Scanning royal combinations…" : <>Reveal my raja yogas <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="raja-yogas-result">
            {/* === D1 + D9 CHARTS === */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-2xl p-5 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="d1-chart-card">
                <div className="label-eyebrow text-[#7C2D12]">D1 · Rasi (Lagna {data.d1_lagna_rashi})</div>
                <div className="mt-4 flex justify-center">
                  <KundaliChart kundali={data.d1_chart} size={300} />
                </div>
                <p className="text-xs text-muted mt-3 text-center font-light">Your foundational chart — material world, physical action, day-to-day kingdom.</p>
              </div>
              <div className="rounded-2xl p-5 bg-gradient-to-br from-[#F0F4FF] to-[#E0EAFF] border border-indigo-200" data-testid="d9-chart-card">
                <div className="label-eyebrow text-indigo-900">D9 · Navamsa (Lagna {data.d9_lagna_rashi})</div>
                <div className="mt-4 flex justify-center">
                  <KundaliChart kundali={data.d9_chart} size={300} />
                </div>
                <p className="text-xs text-muted mt-3 text-center font-light">Soul-level chart — yogas firing here activate strongly after age 30.</p>
              </div>
            </section>

            {/* === SUMMARY === */}
            <section className="rounded-2xl p-6 bg-[#FAF4E8] border border-soft" data-testid="raja-yogas-summary">
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div className="label-eyebrow text-muted">Royal yogas detected</div>
                <div className="text-sm text-muted">
                  <span className="text-amber-900 font-medium">{data.summary.very_high} very-high</span> ·{" "}
                  <span className="text-emerald-900 font-medium">{data.summary.high} high</span> ·{" "}
                  <span className="text-indigo-900 font-medium">{data.summary.moderate} moderate</span>
                  {data.summary.warnings > 0 && <> · <span className="text-rose-900 font-medium">{data.summary.warnings} weakness</span></>}
                </div>
              </div>
              {data.summary.total === 0 && data.summary.warnings === 0 && (
                <div className="mt-6 text-center text-indigo-900 py-8">
                  <ShieldCheck size={32} weight="duotone" className="mx-auto text-indigo-600" />
                  <p className="font-serif-display text-2xl mt-2 font-light">No major raja yogas fire in either chart</p>
                  <p className="text-sm text-muted mt-1">Your chart strength flows through dasha effects and house lordships — book a 1:1 reading to map the path forward.</p>
                </div>
              )}
            </section>

            {/* === D1 YOGAS === */}
            <PaywallGate
              tool="raja_yogas"
              toolLabel="Raja Yogas · Power & Authority"
              birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
              report={data}
            >
              <div className="space-y-10">
              {data.d1_yogas?.length > 0 && (
              <section className="space-y-5">
                <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                  <Sun size={14} weight="duotone" /> D1 · Rasi chart yogas
                </div>
                {data.d1_yogas.map((y, i) => <YogaCard key={`d1-${i}`} y={y} />)}
              </section>
            )}

            {/* === D9 YOGAS === */}
            {data.d9_yogas?.length > 0 && (
              <section className="space-y-5">
                <div className="label-eyebrow text-indigo-900 flex items-center gap-2">
                  <Star size={14} weight="duotone" /> D9 · Navamsa chart yogas <span className="text-muted">· activate after age 30</span>
                </div>
                {data.d9_yogas.map((y, i) => <YogaCard key={`d9-${i}`} y={y} />)}
              </section>
            )}

            {/* === JAIMINI CHARA KARAKAS === */}
            {data.chara_karakas?.length > 0 && (
              <section className="rounded-2xl p-6 bg-gradient-to-br from-[#F5EDFE] to-[#E9DDFB] border border-violet-200" data-testid="chara-karakas">
                <div className="label-eyebrow text-violet-900 flex items-center gap-2">
                  <Eye size={14} weight="duotone" /> Jaimini Chara Karakas
                </div>
                <h2 className="font-serif-display text-3xl mt-2 font-light text-[#3F2D1B]">Your soul's cast of characters</h2>
                <p className="text-xs text-muted mt-2 font-light">Ranked by exact degree-in-sign. Highest degree = Atmakaraka (the soul itself).</p>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.chara_karakas.map((k) => (
                    <div key={k.role_abbr} className="rounded-xl p-4 bg-white border border-violet-200" data-testid={`karaka-${k.role_abbr.toLowerCase()}`}>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-violet-200 text-violet-950">{k.role_abbr}</span>
                        <span className="font-medium text-[#3F2D1B]">{k.planet}</span>
                        <span className="text-xs text-muted">in {k.rashi} {k.degrees_in_rashi}°</span>
                      </div>
                      <p className="text-sm font-serif-display font-light text-[#3F2D1B] mt-1">{k.role_name}</p>
                      <p className="text-xs text-muted mt-1 leading-snug">{k.meaning}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === KARAKAMSA === */}
            {data.karakamsa?.karakamsa_rashi && (
              <section className="rounded-2xl p-6 bg-gradient-to-br from-[#FFF0F5] to-[#FFE3EC] border border-rose-200" data-testid="karakamsa">
                <div className="label-eyebrow text-rose-900 flex items-center gap-2">
                  <Crown size={14} weight="duotone" /> Karakamsa Lagna
                </div>
                <h2 className="font-serif-display text-3xl mt-2 font-light text-[#3F2D1B]">
                  Your soul's seat: <span className="text-rose-900">{data.karakamsa.karakamsa_rashi}</span>
                </h2>
                <p className="text-xs text-muted mt-2">
                  Atmakaraka sits in {data.karakamsa.karakamsa_rashi} (D9). This rashi becomes your Karakamsa Lagna — Jaimini reads planets here, in the 5th and 9th from it, to reveal your true profession and dharma.
                </p>
                {data.karakamsa.planets_in_karakamsa?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-rose-900">Planets in Karakamsa:</p>
                    <p className="text-sm text-[#3F2D1B] mt-1">{data.karakamsa.planets_in_karakamsa.join(", ")}</p>
                  </div>
                )}
                {data.karakamsa_meanings?.length > 0 && (
                  <ul className="mt-3 space-y-2 text-sm">
                    {data.karakamsa_meanings.map((m, i) => (
                      <li key={i} className="flex gap-3 leading-snug">
                        <span className="text-rose-700 mt-0.5">✦</span>
                        <span className="text-[#3F2D1B]">{m}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(data.karakamsa.planets_in_5th_from_karakamsa?.length > 0 || data.karakamsa.planets_in_9th_from_karakamsa?.length > 0) && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    {data.karakamsa.planets_in_5th_from_karakamsa?.length > 0 && (
                      <div className="rounded-md p-3 bg-white border border-rose-200">
                        <p className="font-medium text-rose-900">5th from Karakamsa</p>
                        <p className="text-[#3F2D1B] mt-1">{data.karakamsa.planets_in_5th_from_karakamsa.join(", ")} <span className="text-muted">— creative output, mantra-siddhi, education</span></p>
                      </div>
                    )}
                    {data.karakamsa.planets_in_9th_from_karakamsa?.length > 0 && (
                      <div className="rounded-md p-3 bg-white border border-rose-200">
                        <p className="font-medium text-rose-900">9th from Karakamsa</p>
                        <p className="text-[#3F2D1B] mt-1">{data.karakamsa.planets_in_9th_from_karakamsa.join(", ")} <span className="text-muted">— dharma, guru-energy, deities you serve</span></p>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* === JAIMINI RAJA YOGAS === */}
            {data.jaimini_yogas?.length > 0 && (
              <section className="space-y-5">
                <div className="label-eyebrow text-violet-900 flex items-center gap-2">
                  <Crown size={14} weight="duotone" /> Jaimini Raja Yogas
                </div>
                {data.jaimini_yogas.map((y, i) => <YogaCard key={`jai-${i}`} y={y} />)}
              </section>
            )}
              </div>
            </PaywallGate>

            <ToolAstrologerCta
              requiredTechnique="Jaimini"
              pitch="Get a live walk-through of which raja yoga activates in your current dasha — with a Jaimini specialist"
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
